import React, { useState, type FC } from 'react';
import { NavLink } from 'react-router-dom';

import {
  faCog,
  faCode,
  faCubes,
  faDollar,
  faGavel,
  faHome,
  faTruck,
  faUser,
  faEdit,
  faClock,
  faCalendar,
  faBuilding,
  faPersonChalkboard,
  faUsers,
  faHandshake,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';

import { allowed } from '@admin/components/helpers';
import { useNavBarFavorites } from '@admin/hooks/use_admin_nav_favorites';
import { useLocation } from '@admin/hooks/use_location';

import { NavBarDropdown } from './nav_bar_dropdown';
import { NavBarFavoriteButton } from './nav_bar_favorite_button';
import { NavBarLink } from './nav_bar_link';
import { NavBarNotificationCountBadge } from './nav_bar_notification_count_badge';
import { NavBarSection } from './nav_bar_section';
import { NavBarSMSInboxCountBadge } from './nav_bar_sms_inbox_count_badge';
import { NavBarWarehouseTaskCountBadge } from './nav_bar_warehouse_task_count_badge';

const SEPARATOR_LINK: NavBarLink = {
  text: '|',
  to: null,
  match: null,
  state: null,
};

const HOME_LINK: NavBarLink = {
  text: 'Tickets',
  to: '/',
  match: /^\/$/,
  state: 'inbox',
};

const FAVORITE_LINK: NavBarLink = {
  text: 'Pin pages from other tabs using the star icon',
  to: null,
  match: null,
  state: null,
  hint: true,
};

const LEADS_LINK: NavBarLink = {
  text: 'Leads',
  to: '/leads',
  match: /^\/leads/,
  state: 'leads',
};

const ACCOUNTS_LINK: NavBarLink = {
  text: 'Accounts',
  to: '/accounts',
  match: /^\/accounts/,
  state: 'accounts',
};

const COLLECTIONS_LINK: NavBarLink = {
  text: 'Collections',
  to: '/collections',
  match: /^\/collections/,
  state: 'collections',
};

const SMS_INBOX_LINK: NavBarLink = {
  text: 'SMS',
  to: '/smsinbox',
  match: /^\/smsinbox/,
  state: 'smsInbox',
};

const VOICEMAILS_LINK: NavBarLink = {
  text: 'Voicemails',
  to: '/voicemails',
  match: /^\/voicemails/,
  state: 'voicemails',
};

const PHONE_CALLS_LINK: NavBarLink = {
  text: 'Calls',
  to: '/phone_calls',
  match: /^\/phone_calls/,
  state: 'phoneCalls',
};

const CLAIMS_LINK: NavBarLink = {
  text: 'Claims',
  to: '/claims',
  match: /^\/claims/,
  state: 'claims',
};

const CUSTOMER_TICKETS_LINK: NavBarLink = {
  text: 'Customer Tickets',
  to: '/customer_tickets',
  match: /^\/customer_tickets/,
  state: 'customerTickets',
};

const SELF_STORAGE_UNITS_LINK: NavBarLink = {
  text: 'Units',
  to: '/self_storage/units',
  match: /^\/self_storage\/units/,
  state: 'selfStorageUnitsIndex',
};

const SELF_STORAGE_FACILITIES_LINK: NavBarLink = {
  text: 'Facilities',
  to: '/self_storage/facilities',
  match: /^\/self_storage\/facilities/,
  state: 'selfStorageFacilities',
};

const ORDERS_LINK: NavBarLink = {
  text: 'Orders',
  to: '/orders',
  match: /^\/orders/,
  state: 'orders',
};

const DISPATCHES_LINK: NavBarLink = {
  text: 'Dispatches',
  to: '/dispatches',
  match: /^\/dispatches/,
  state: 'dispatches',
};

const DISPATCHER_LINK: NavBarLink = {
  text: 'Dispatcher',
  to: '/dispatcher',
  match: /^\/dispatcher/,
  state: 'dispatcher',
};

const MAP_LINK: NavBarLink = {
  text: 'Map',
  to: '/map',
  match: /^\/map/,
  state: 'map',
};

const TRANSFERS_LINK: NavBarLink = {
  text: 'Transfers',
  to: '/transfers',
  match: /^\/(transfers|transferpools|transferables)/,
  state: 'transfers',
};

const PTO_REQUESTS_LINK: NavBarLink = {
  text: 'PTO Requests',
  to: '/pto_requests',
  match: /^\/pto_requests/,
  state: 'ptoRequests',
};

const VEHICLES_LINK: NavBarLink = {
  text: 'Fleet',
  to: '/vehicles',
  match: /^\/vehicles/,
  state: 'vehicles',
};

const CALENDAR_LINK: NavBarLink = {
  text: 'View Calendar',
  to: '/calendar',
  match: /^\/calendar/,
  state: 'calendar',
};

const SHIFTS_LINK: NavBarLink = {
  text: 'Shifts',
  to: '/shifts',
  match: /^\/shifts/,
  state: 'shifts',
};

const ITEMS_LINK: NavBarLink = {
  text: 'Items Explorer',
  to: '/items',
  match: /^\/items/,
  state: 'itemsExplorer',
};

const VEHICLE_ROUTE_MATCHER_LINK: NavBarLink = {
  text: 'Vehicle Route Matcher',
  to: '/vehicle_route_matcher',
  match: /^\/vehicle_route_matcher/,
  state: 'vehicleRouteMatcher',
};

const NIGHT_TRANSPORT_PLANNER_LINK: NavBarLink = {
  text: 'Night Transport Planner',
  to: '/night_transport/planner',
  match: /^\/night_transport\/planner/,
  state: 'nightTransportPlanner',
};

const AUCTIONS_LINK: NavBarLink = {
  text: 'Auctions',
  to: '/auctions',
  match: /^\/auctions/,
  state: 'auctions',
};

const WAREHOUSE_TASKS_LINK: NavBarLink = {
  text: 'Warehouse Tasks',
  to: '/warehouse_tasks',
  match: /^\/warehouse_tasks/,
  state: 'warehouseTasks',
};

const USERS_LINK: NavBarLink = {
  text: 'Users',
  to: '/users',
  match: /^\/users/,
  state: 'users',
};

const FACILITIES_LINK: NavBarLink = {
  text: 'Facilities',
  to: '/facilities',
  match: /^\/facilities/,
  state: 'facilities',
};

const TICKET_MANAGEMENT_LINK: NavBarLink = {
  text: 'Task Groups',
  to: '/ticket_management',
  match: /^\/ticket_management/,
  state: 'ticketmanagement',
};

const PRICING_UPLOADS_LINK: NavBarLink = {
  text: 'Pricing Uploads',
  to: '/pricing_uploads',
  match: /^\/pricing_uploads/,
  state: 'pricingUploads',
};

const INGESTIONS_LINK: NavBarLink = {
  text: 'Ingestions',
  to: '/ingestions',
  match: /^\/ingestions/,
  state: 'ingestions',
};

const REGIONS_LINK: NavBarLink = {
  text: 'Regions',
  to: '/regions',
  match: /^\/regions/,
  state: 'regions',
};

const PALLET_TYPES_LINK: NavBarLink = {
  text: 'Pallet Types',
  to: '/pallet_types',
  match: /^\/pallet_types/,
  state: 'palletTypes',
};

const NOTES_UPLOADER_LINK: NavBarLink = {
  text: 'Notes Uploader',
  to: '/notes_uploader',
  match: /^\/notes_uploader/,
  state: 'notesUploader',
};

const DOOR_TO_DOOR_RATE_ADJUSTMENTS_LINK: NavBarLink = {
  text: 'Rate Adjustments',
  to: '/door_to_door/rate_adjustments',
  match: /^\/door_to_door\/rate_adjustments/,
  state: 'doorToDoorRateAdjustments',
};

const HRS_ROLE_DEFAULTS_LINK: NavBarLink = {
  text: 'HRS Role Defaults',
  to: '/hrs_role_defaults',
  match: /^\/hrs_role_defaults/,
  state: 'hrsRoleDefaults',
};

const HRS_PROFILE_DEFAULTS_LINK: NavBarLink = {
  text: 'HRS Profile Defaults',
  to: '/hrs_profile_defaults',
  match: /^\/hrs_profile_defaults/,
  state: 'hrsProfileDefaults',
};

const HRS_LOCATIONS_LINK: NavBarLink = {
  text: 'HRS Locations',
  to: '/hrs_locations',
  match: /^\/hrs_locations/,
  state: 'hrsLocations',
};

const FEATURE_FLAGS_LINK: NavBarLink = {
  text: 'Feature Flags',
  to: '/feature_flags',
  match: /^\/feature_flags/,
  state: 'featureFlags',
};

const TEAMS_LINK: NavBarLink = {
  text: 'Teams',
  to: '/teams',
  match: /^\/teams/,
  state: 'teams',
};

const ZENDESK_UPLOADS_LINK: NavBarLink = {
  text: 'Zendesk Uploads',
  to: '/zendesk/uploads',
  match: /^\/zendesk\/uploads/,
  state: 'zendeskUploads',
};

const ACCESS_CONTROL_PERMISSIONS_LINK: NavBarLink = {
  text: 'Permissions',
  to: '/access_control/permissions',
  match: /^\/access_control\/permissions/,
  state: 'accessControlPermissions',
};

const ACCESS_CONTROL_CATALOG_LINK: NavBarLink = {
  text: 'Catalog',
  to: '/access_control/catalog',
  match: /^\/access_control\/catalog/,
  state: 'accessControlCatalog',
};

const REFERRALS_LINK: NavBarLink = {
  text: 'Referrals',
  to: '/marketing/referrals',
  match: /^\/marketing\/referrals/,
  state: 'referrals',
};

const GRATUITIES_LINK: NavBarLink = {
  text: 'Tips',
  to: '/gratuities',
  match: /^\/gratuities/,
  state: 'gratuities',
};

const AFFILIATES_LINK: NavBarLink = {
  text: 'Affiliates',
  to: '/marketing/affiliates',
  match: /^\/marketing\/affiliates/,
  state: 'affiliates',
};

const REDIRECTS_LINK: NavBarLink = {
  text: 'URL Redirects',
  to: '/marketing/redirects',
  match: /^\/marketing\/redirects/,
  state: 'redirects',
};

const COUPONS_LINK: NavBarLink = {
  text: 'Coupons',
  to: '/marketing/coupons',
  match: /^\/marketing\/coupons/,
  state: 'coupons',
};

const UTM_PHONES_LINK: NavBarLink = {
  text: 'Set Campaign to Phone',
  to: '/marketing/utm_phones/new',
  match: /^\/marketing\/utm_phones/,
  state: 'utmPhones',
};

const QR_CODE_GENERATOR_LINK: NavBarLink = {
  text: 'QR Code Generator',
  to: '/marketing/qr_codes',
  match: /^\/marketing\/qr_codes/,
  state: 'qrCodeGenerator',
};

const ITERABLE_EXPORTS_LINK: NavBarLink = {
  text: 'Email Exports',
  to: '/marketing/email_exports',
  match: /^\/marketing\/email_exports/,
  state: 'iterableExports',
};

const TIME_CLOCK_LINK: NavBarLink = {
  text: 'Time Clock',
  to: '/timetable/time_clocks',
  match: /^\/timetable\/time_clocks/,
  state: 'timeClock',
};

const OPS_AUDIT_CONFIGURATIONS_LINK: NavBarLink = {
  text: 'Ops Audit Configurations',
  to: '/ops_audit/configurations',
  match: /^\/ops_audit\/configurations/,
  state: 'opsAuditConfigurations',
};

const LOGISTICS_LOADS_LINK: NavBarLink = {
  text: 'Loads',
  to: '/logistics/loads',
  match: /^\/logistics\/loads/,
  state: 'loads',
};

const LOGISTICS_PURCHASE_ORDERS_LINK: NavBarLink = {
  text: 'Purchase Orders',
  to: '/logistics/purchase_orders',
  match: /^\/logistics\/purchase_orders/,
  state: 'purchaseOrders',
};

const LOGISTICS_ACCOUNT_NEW_LINK: NavBarLink = {
  text: 'Create Account',
  to: '/logistics/accounts/new',
  match: /^\/logistics\/accounts/,
  state: 'createLogisticsAccount',
};

const LANDING_LOCATIONS_PAGES_LINK: NavBarLink = {
  text: 'Landing Locations Pages',
  to: '/landing/location/pages',
  match: /^\/landing\/location\/pages/,
  state: 'landingLocationPageList',
};

const LANDING_LOCATIONS_STATES_LINK: NavBarLink = {
  text: 'Landing Locations States',
  to: '/landing/location/states',
  match: /^\/landing\/location\/states/,
  state: 'landingLocationStateList',
};

const LANDING_LOCATIONS_CITIES_LINK: NavBarLink = {
  text: 'Landing Locations Cities',
  to: '/landing/location/cities',
  match: /^\/landing\/location\/cities/,
  state: 'landingLocationCityList',
};

const LANDING_LOCATIONS_NEIGHBORHOODS_LINK: NavBarLink = {
  text: 'Landing Locations Neighborhoods',
  to: '/landing/location/neighborhoods',
  match: /^\/landing\/location\/neighborhoods/,
  state: 'landingLocationNeighborhoodList',
};

const LANDING_LOCATIONS_ROUTES_LINK: NavBarLink = {
  text: 'Landing Locations Routes',
  to: '/landing/location/routes',
  match: /^\/landing\/location\/routes/,
  state: 'landingLocationRouteList',
};

const GLOBAL_UNAVAILABILITY_LINK: NavBarLink = {
  text: 'Global Unavailability',
  to: '/global_unavailability',
  match: /^\/global_unavailability/,
  state: 'globalUnavailability',
};

const JOB_AVAILABILITY_LINK: NavBarLink = {
  text: 'Job Availability',
  to: '/job_availability',
  match: /^\/job_availability/,
  state: 'jobAvailability',
};

const WAREHOUSE_CAPACITY_LINK: NavBarLink = {
  text: 'Warehouse Capacity',
  to: '/warehouse_capacity',
  match: /^\/warehouse_capacity/,
  state: 'warehouseCapacity',
};

const AVAILABILITY_LABOR_ADJUSTMENT_LINK: NavBarLink = {
  text: 'Availability Labor Adjustment',
  to: '/availability_labor_adjustment',
  match: /^\/availability_labor_adjustment/,
  state: 'availabilityLaborAdjustment',
};

const AVAILABILITY_RESCHEDULE_OFFERS_LINK: NavBarLink = {
  text: 'Availability Reschedule Offers',
  to: '/availability_reschedule_offers',
  match: /^\/availability_reschedule_offers/,
  state: 'availabilityRescheduleOffers',
};

const TRAINING_UPLOADS_LINK: NavBarLink = {
  text: 'Uploads',
  to: '/training_uploads',
  match: /^\/training_uploads/,
  state: 'trainingUploads',
};

const USER_SKILLS_LINK: NavBarLink = {
  text: 'User Qualifications',
  to: '/user_skills',
  match: /^\/user_skills/,
  state: 'userSkills',
};

const SOP_DOCUMENTS_LINK: NavBarLink = {
  text: 'SOPs',
  to: '/sop_documents',
  match: /^\/sop_documents/,
  state: 'sopDocuments',
};

const ENGINEERING_ROADMAP_LINK: NavBarLink = {
  text: 'Engineering Roadmap',
  to: '/engineering_roadmap',
  match: /^\/engineering_roadmap/,
  state: 'engineeringRoadmap',
};

const HIRING_JOB_LISTINGS_LINK: NavBarLink = {
  text: 'Job Listings',
  to: '/hiring/job_listings',
  match: /^\/hiring\/job_listings/,
  state: 'hiringJobListings',
};

const HIRING_ROLES_LINK: NavBarLink = {
  text: 'Roles',
  to: '/hiring/roles',
  match: /^\/hiring\/roles/,
  state: 'hiringRoles',
};

const HIRING_PRE_SCREEN_QUESTIONS_LINK: NavBarLink = {
  text: 'Pre-Screen Questions',
  to: '/hiring/pre_screen_questions',
  match: /^\/hiring\/pre_screen_questions/,
  state: 'hiringPreScreenQuestions',
};

const HIRING_INTERVIEW_TIME_SLOTS_LINK: NavBarLink = {
  text: 'Interview Time Slots',
  to: '/hiring/interview_time_slots',
  match: /^\/hiring\/interview_time_slots/,
  state: 'hiringInterviewTimeSlots',
};

const HIRING_CANDIDATES_LINK: NavBarLink = {
  text: 'Candidates',
  to: '/hiring/candidates',
  match: /^\/hiring\/candidates/,
  state: 'hiringCandidates',
};

const PARTNERSHIPS_LINK: NavBarLink = {
  text: 'Partnerships',
  to: '/partnerships',
  match: /^\/partnerships/,
  state: 'partnershipsPartners',
};

const HOME_SECTION: NavBarSection = {
  name: 'home',
  icon: faHome,
  label: 'Home',
  links: [HOME_LINK],
  match: /^\/$/,
};

const ACCOUNT_SECTION: NavBarSection = {
  name: 'account',
  icon: faUser,
  label: 'Account',
  links: [
    LEADS_LINK,
    ACCOUNTS_LINK,
    COLLECTIONS_LINK,
    SEPARATOR_LINK,
    SMS_INBOX_LINK,
    VOICEMAILS_LINK,
    PHONE_CALLS_LINK,
    CLAIMS_LINK,
    CUSTOMER_TICKETS_LINK,
  ],
  match: /^\/(accounts|leads|collections|smsinbox|voicemails|phone_calls|claims|customer_tickets)/,
};

const SELF_STORAGE_SECTION: NavBarSection = {
  name: 'selfStorage',
  icon: faBuilding,
  label: 'Self-Storage',
  links: [SELF_STORAGE_UNITS_LINK, SELF_STORAGE_FACILITIES_LINK],
  match: /^\/(self_storage)/,
};

const JOBS_SECTION: NavBarSection = {
  name: 'jobs',
  icon: faTruck,
  label: 'Jobs',
  links: [
    ORDERS_LINK,
    DISPATCHES_LINK,
    DISPATCHER_LINK,
    MAP_LINK,
    TRANSFERS_LINK,
    PTO_REQUESTS_LINK,
    VEHICLES_LINK,
    CALENDAR_LINK,
    SHIFTS_LINK,
    ITEMS_LINK,
    VEHICLE_ROUTE_MATCHER_LINK,
    NIGHT_TRANSPORT_PLANNER_LINK,
    AUCTIONS_LINK,
    WAREHOUSE_TASKS_LINK,
  ],
  match:
    /^\/(orders|dispatches|dispatcher|map|transfers|pto_requests|vehicles|calendar|shifts|items|vehicle_route_matcher|night_transport|auctions|warehouse_tasks)/,
};

const SETTINGS_SECTION: NavBarSection = {
  name: 'settings',
  icon: faCog,
  label: 'Settings',
  links: [
    USERS_LINK,
    TEAMS_LINK,
    FACILITIES_LINK,
    REGIONS_LINK,
    INGESTIONS_LINK,
    TICKET_MANAGEMENT_LINK,
    PRICING_UPLOADS_LINK,
    PALLET_TYPES_LINK,
    NOTES_UPLOADER_LINK,
    DOOR_TO_DOOR_RATE_ADJUSTMENTS_LINK,
    HRS_ROLE_DEFAULTS_LINK,
    HRS_PROFILE_DEFAULTS_LINK,
    HRS_LOCATIONS_LINK,
    FEATURE_FLAGS_LINK,
    ZENDESK_UPLOADS_LINK,
  ],
  match:
    /^\/(users|warehouses|facilities|ticket_management|pricing_uploads|ingestions|depots|regions|pallet_types|notes_uploader|door_to_door|hrs_role_defaults|hrs_profile_defaults|hrs_locations|feature_flags|teams|zendesk)/, // warehouses/depots URLs redirect to facilities
};

const ACCESS_CONTROL_SECTION: NavBarSection = {
  name: 'accessControl',
  icon: faShieldHalved,
  label: 'Access Control',
  links: [ACCESS_CONTROL_PERMISSIONS_LINK, ACCESS_CONTROL_CATALOG_LINK],
  match: /^\/(access_control)/,
};

const MARKETING_SECTION: NavBarSection = {
  name: 'marketing',
  icon: faDollar,
  label: 'Marketing',
  links: [
    REFERRALS_LINK,
    GRATUITIES_LINK,
    AFFILIATES_LINK,
    REDIRECTS_LINK,
    COUPONS_LINK,
    UTM_PHONES_LINK,
    QR_CODE_GENERATOR_LINK,
    ITERABLE_EXPORTS_LINK,
  ],
  match: /^\/(marketing|gratuities)/,
};

const TIMETABLE_SECTION: NavBarSection = {
  name: 'timetable',
  icon: faClock,
  label: 'Timetable',
  links: [TIME_CLOCK_LINK],
  match: /^\/(timetable)/,
};

const OPS_AUDIT_SECTION: NavBarSection = {
  name: 'opsAudit',
  icon: faGavel,
  label: 'Ops Audit',
  links: [OPS_AUDIT_CONFIGURATIONS_LINK],
  match: /^\/(ops_audit)/,
};

const LOGISTICS_SECTION: NavBarSection = {
  name: 'logistics',
  icon: faCubes,
  label: '3PL',
  links: [LOGISTICS_LOADS_LINK, LOGISTICS_PURCHASE_ORDERS_LINK, LOGISTICS_ACCOUNT_NEW_LINK],
  match: /^\/(logistics)/,
};

const CONTENT_SECTION: NavBarSection = {
  name: 'content',
  icon: faEdit,
  label: 'CMS',
  links: [
    LANDING_LOCATIONS_PAGES_LINK,
    LANDING_LOCATIONS_STATES_LINK,
    LANDING_LOCATIONS_CITIES_LINK,
    LANDING_LOCATIONS_NEIGHBORHOODS_LINK,
    LANDING_LOCATIONS_ROUTES_LINK,
  ],
  match: /^\/(landing)/,
};

const APPOINTMENT_AVAILABILITY_SECTION: NavBarSection = {
  name: 'appointmentAvailability',
  icon: faCalendar,
  label: 'Availability',
  links: [
    GLOBAL_UNAVAILABILITY_LINK,
    JOB_AVAILABILITY_LINK,
    WAREHOUSE_CAPACITY_LINK,
    AVAILABILITY_LABOR_ADJUSTMENT_LINK,
    AVAILABILITY_RESCHEDULE_OFFERS_LINK,
  ],
  match:
    /^\/(global_unavailability|job_availability|warehouse_capacity|availability_labor_adjustment|availability_reschedule_offers)/,
};

const TRAINING_SECTION: NavBarSection = {
  name: 'training',
  icon: faPersonChalkboard,
  label: 'Training',
  links: [TRAINING_UPLOADS_LINK, USER_SKILLS_LINK, SOP_DOCUMENTS_LINK],
  match: /^\/(training_uploads|user_skills|sop_documents)/,
};

const ENGINEERING_SECTION: NavBarSection = {
  name: 'engineering',
  icon: faCode,
  label: 'Engineering',
  links: [ENGINEERING_ROADMAP_LINK],
  match: /^\/(engineering_roadmap)/,
};

const HIRING_SECTION: NavBarSection = {
  name: 'hiring',
  icon: faUsers,
  label: 'Hiring',
  links: [
    HIRING_JOB_LISTINGS_LINK,
    HIRING_ROLES_LINK,
    HIRING_PRE_SCREEN_QUESTIONS_LINK,
    HIRING_INTERVIEW_TIME_SLOTS_LINK,
    HIRING_CANDIDATES_LINK,
  ],
  match: /^\/(hiring)/,
};

const PARTNERSHIPS_SECTION: NavBarSection = {
  name: 'partnerships',
  icon: faHandshake,
  label: 'Partnerships',
  links: [PARTNERSHIPS_LINK],
  match: /^\/(partnerships)/,
};

const SECTIONS: NavBarSection[] = [
  HOME_SECTION,
  ACCOUNT_SECTION,
  SELF_STORAGE_SECTION,
  JOBS_SECTION,
  SETTINGS_SECTION,
  ACCESS_CONTROL_SECTION,
  MARKETING_SECTION,
  TIMETABLE_SECTION,
  OPS_AUDIT_SECTION,
  LOGISTICS_SECTION,
  CONTENT_SECTION,
  APPOINTMENT_AVAILABILITY_SECTION,
  TRAINING_SECTION,
  ENGINEERING_SECTION,
  HIRING_SECTION,
  PARTNERSHIPS_SECTION,
];

const LINKS: NavBarLink[] = SECTIONS.map((section) => section.links).flat();

export const NavBar: FC = () => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const [favorites, setFavorites] = useNavBarFavorites();

  const sections = SECTIONS.filter((section) => section.links.some((link) => link.state && allowed(link.state)));

  const current = sections.find((section) => section.links.some((link) => link.match?.test(location.pathname)));

  const links = (() => {
    if (!current) return [];
    if (current !== HOME_SECTION) return current.links;
    if (favorites.size) return [...current.links, ...LINKS.filter((link) => link.state && favorites.has(link.state))];
    else return [...current.links, FAVORITE_LINK];
  })().filter((link) => !link.state || allowed(link.state));

  return (
    <header id="navbar">
      <div id="navbar-container" className="primary_nav boxed">
        <div className="navbar-header">
          <a href="/" className="navbar-brand">
            <div className="brand-title">
              <span className="brand-text no-hover">Clutter</span>
            </div>
          </a>
        </div>

        <div className="navbar-content clearfix">
          <ul className="nav navbar-top-links pull-left">
            {sections.map((section, key) => {
              const link = section.links.filter((link) => !link.state || allowed(link.state)).find((link) => link.to);
              if (!link) return null;
              if (!link.to) return null;

              return (
                <li key={key} className={cn(current?.name === section.name && 'active')}>
                  <NavLink to={link.to} title={section.label}>
                    <FontAwesomeIcon icon={section.icon} aria-label={section.label} />
                    <> </>
                    <span className="nav-label">{section.label}</span>
                    {section === HOME_SECTION && (
                      <>
                        <> </>
                        <NavBarNotificationCountBadge />
                      </>
                    )}
                    {section === JOBS_SECTION && (
                      <>
                        <> </>
                        <NavBarWarehouseTaskCountBadge />
                      </>
                    )}
                    {section === ACCOUNT_SECTION && (
                      <>
                        <> </>
                        <NavBarSMSInboxCountBadge />
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <ul className="nav navbar-top-links pull-right">
            <NavBarDropdown />
          </ul>
        </div>
      </div>

      <div id="navbar-container" className="secondary_nav boxed">
        <div className="navbar-content clearfix">
          <button
            type="button"
            className={cn('navbar-toggle', !expanded && 'collapsed')}
            onClick={() => setExpanded((expanded) => !expanded)}
          >
            <span className="sr-only">Menu</span>
            <span className="icon-bar" />
            <span className="icon-bar" />
            <span className="icon-bar" />
          </button>

          <div className={cn('navbar-collapse', !expanded && 'collapse')} id="sub-navbar-collapse">
            <ul className="nav navbar-nav navbar-top-links">
              {links.map((link, key) => (
                <li
                  key={key}
                  className={cn(link.match?.test(location.pathname) && 'active', link.hint && 'nav-favorites-hint')}
                >
                  {link.to ? (
                    <>
                      <NavLink to={link.to}>
                        <span>{link.text}</span>
                        {link === HOME_LINK && (
                          <>
                            {' '}
                            <NavBarNotificationCountBadge />
                          </>
                        )}
                        {link === SMS_INBOX_LINK && (
                          <>
                            {' '}
                            <NavBarSMSInboxCountBadge />
                          </>
                        )}
                        {link === WAREHOUSE_TASKS_LINK && (
                          <>
                            {' '}
                            <NavBarWarehouseTaskCountBadge />
                          </>
                        )}
                      </NavLink>
                      {link.state && (
                        <NavBarFavoriteButton link={link} favorite={favorites.has(link.state)} onSave={setFavorites} />
                      )}
                    </>
                  ) : (
                    <span className={cn(link.hint && 'nav-favorites-hint-text')}>{link.text}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};
